import React from 'react';
import { ReportMetadata } from '../../types';
import { PlankopfForm, plankopfAusStammdaten, plankopfBereit } from './uebernahme';
import { mitPlanZeitachse, planHatZeitachse } from './types';
import { Button } from '../Button';
import { RefreshCw } from 'lucide-react';

// Schritt 1 des Anlage-Assistenten: Plankopf und Bauzeit. Reine Darstellung —
// der Zustand liegt im Assistenten, gespeichert wird beim Weiterblättern.

interface Props {
  form: PlankopfForm;
  onChange: (form: PlankopfForm) => void;
  reportMeta?: ReportMetadata;
  projektName?: string;
}

const inputCls = 'w-full text-sm p-2 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

export const WizardStepPlankopf: React.FC<Props> = ({ form, onChange, reportMeta, projektName }) => {
  const setzen = (feld: keyof PlankopfForm, wert: string) => onChange({ ...form, [feld]: wert });
  const bereit = plankopfBereit(form);
  // Planungsphase: ohne Bauzeitenplan druckt der Plan ohne Zeitachse, die
  // Bauzeit ist dann keine Pflicht (plankopfBereit).
  const mitZeitachse = planHatZeitachse(form.darstellung);

  const fehlend = [
    !(form.bauherr_name || '').trim() && 'Bauherr',
    !(form.baumassnahme_name || '').trim() && 'Baumaßnahme',
    mitZeitachse && !(form.zeitraum_von || '').trim() && 'Bauzeit von',
    mitZeitachse && !(form.zeitraum_bis || '').trim() && 'Bauzeit bis',
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-slate-600 max-w-2xl">
          Der Plankopf steht auf jedem Ausdruck. Die <strong>Bauzeit</strong> steuert außerdem die
          Terminautomatik der Arbeitsschritte und die Zeitachse im PDF; ohne sie bleiben die
          Zeitspalten leer.
        </p>
        {reportMeta && (
          <Button
            variant="secondary"
            onClick={() => onChange(plankopfAusStammdaten(reportMeta, projektName, form))}
            className="shrink-0 text-sm"
            title="Bauherr, Baumaßnahme, SiGeKo und Bauzeit aus den Projekt-Stammdaten übernehmen"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Stammdaten erneut übernehmen
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Bauherr *</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name *</label>
            <input data-tutorial="sigeplan.bauherr" className={inputCls} value={form.bauherr_name || ''} onChange={e => setzen('bauherr_name', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Adresse</label>
            <input className={inputCls} value={form.bauherr_adresse || ''} onChange={e => setzen('bauherr_adresse', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Baumaßnahme *</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name *</label>
            <input className={inputCls} value={form.baumassnahme_name || ''} onChange={e => setzen('baumassnahme_name', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Adresse (Grundlage für den Notrufplan)</label>
            <input className={inputCls} value={form.baumassnahme_adresse || ''} onChange={e => setzen('baumassnahme_adresse', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Bauzeit{mitZeitachse && ' *'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">von{mitZeitachse && ' *'}</label>
            <input data-tutorial="sigeplan.bauzeit-von" type="date" className={inputCls} value={form.zeitraum_von || ''} onChange={e => setzen('zeitraum_von', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">bis{mitZeitachse && ' *'}</label>
            <input type="date" className={inputCls} value={form.zeitraum_bis || ''} onChange={e => setzen('zeitraum_bis', e.target.value)} />
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={!mitZeitachse}
            onChange={e => onChange({ ...form, darstellung: mitPlanZeitachse(form.darstellung, !e.target.checked) })}
          />
          <span>
            Bauzeitenplan liegt noch nicht vor (Planungsphase): Zeitspalte ohne Balken drucken
            <span className="block text-xs text-slate-500 mt-0.5">
              Die Bauzeit darf dann fehlen. Übernommene Arbeitsschritte bekommen keine Zeiträume; im Plan steht
              „Zeiträume folgen mit dem Bauzeitenplan“. Der Haken lässt sich im Plankopf jederzeit entfernen.
            </span>
          </span>
        </label>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plan-Angaben</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Plan-Nr.</label>
            <input className={inputCls} value={form.plan_nr || ''} onChange={e => setzen('plan_nr', e.target.value)} placeholder="z. B. SGP-4711" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Plan-Datum</label>
            <input type="date" className={inputCls} value={form.plan_datum || ''} onChange={e => setzen('plan_datum', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Aufsteller (bearbeitet von)</label>
            <input className={inputCls} value={form.bearbeitet_name || ''} onChange={e => setzen('bearbeitet_name', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">SiGeKo im Plankopf</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name</label>
            <input className={inputCls} value={form.sigeko_name || ''} onChange={e => setzen('sigeko_name', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Mobil</label>
            <input className={inputCls} value={form.sigeko_mobil || ''} onChange={e => setzen('sigeko_mobil', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">E-Mail</label>
            <input className={inputCls} value={form.sigeko_email || ''} onChange={e => setzen('sigeko_email', e.target.value)} />
          </div>
        </div>
      </div>

      {!bereit && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Für „Weiter" fehlt noch: {fehlend.join(', ')}. Über „Später" wird der Plan mit den
          Projekt-Stammdaten angelegt und kann jederzeit ergänzt werden.
        </div>
      )}
    </div>
  );
};
